import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { User, LoginCredentials, AuthResponse, ApiError } from '../../types';
import { authApi } from '../../services/api';
import { setToken, setRefreshToken, setUser, clearAuth } from '../../utils/auth';

interface AuthState {
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
}

const initialState: AuthState = {
  user: null,
  token: null,
  refreshToken: null,
  isAuthenticated: false,
  isLoading: false,
  status: 'idle',
  error: null,
};

// Асинхронные действия
export const login = createAsyncThunk<
  AuthResponse,
  LoginCredentials,
  { rejectValue: string }
>(
  'auth/login',
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await authApi.login(credentials.username, credentials.password);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.detail || 'Ошибка входа. Проверьте введенные данные.'
      );
    }
  }
);

// Тип для данных регистрации
interface RegisterData {
  username: string;
  email: string;
  password1: string;
  password2: string;
}

export const register = createAsyncThunk<
  void,
  RegisterData,
  { rejectValue: string }
>(
  'auth/register',
  async (userData, { rejectWithValue }) => {
    try {
      const response = await authApi.register(userData);
      return response.data;
    } catch (error: any) {
      // Обработка ошибок валидации
      if (error.response?.data) {
        // Преобразуем ошибки валидации в строку
        const errors = Object.entries(error.response.data)
          .map(([field, messages]) => `${field}: ${(messages as string[]).join(' ')}`)
          .join('\n');
        return rejectWithValue(errors);
      }
      return rejectWithValue(
        error.response?.data?.detail || 'Ошибка регистрации. Пожалуйста, попробуйте снова.'
      );
    }
  }
);

interface UpdateProfileData {
  id: number;
  first_name?: string;
  last_name?: string;
  email?: string;
  username?: string;
}

export const updateProfile = createAsyncThunk<
  User,
  UpdateProfileData,
  { rejectValue: string }
>(
  'auth/updateProfile',
  async (profileData, { rejectWithValue }) => {
    try {
      const response = await authApi.updateProfile(profileData.id, profileData);
      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        const errors = Object.entries(error.response.data)
          .map(([field, messages]) => `${field}: ${(messages as string[]).join(' ')}`)
          .join('\n');
        return rejectWithValue(errors);
      }
      return rejectWithValue(
        error.response?.data?.detail || 'Ошибка обновления профиля. Пожалуйста, попробуйте снова.'
      );
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (state) => {
      clearAuth();
      return {
        ...initialState,
        isAuthenticated: false,
      };
    },
    clearError: (state) => {
      state.error = null;
    },
    setCredentials: (state, action: PayloadAction<AuthResponse>) => {
      const { user, access, refresh } = action.payload;
      state.user = user;
      state.token = access;
      state.refreshToken = refresh;
      state.isAuthenticated = true;
      
      // Сохраняем данные в localStorage
      setToken(access);
      setRefreshToken(refresh || '');
      setUser(user);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.status = 'loading';
        state.isLoading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        const { user, access, refresh } = action.payload;
        state.status = 'succeeded';
        state.isLoading = false;
        state.isAuthenticated = true;
        state.user = user;
        state.token = access;
        state.refreshToken = refresh || null;
        state.error = null;
        
        // Сохраняем данные в localStorage
        setToken(access);
        if (refresh) setRefreshToken(refresh);
        setUser(user);
      })
      .addCase(login.rejected, (state, action) => {
        state.status = 'failed';
        state.isLoading = false;
        state.isAuthenticated = false;
        state.error = action.payload as string;
      })
      .addCase(register.pending, (state) => {
        state.status = 'loading';
        state.isLoading = true;
        state.error = null;
      })
      .addCase(register.fulfilled, (state) => {
        state.status = 'succeeded';
        state.isLoading = false;
        state.error = null;
      })
      .addCase(register.rejected, (state, action) => {
        state.status = 'failed';
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(updateProfile.pending, (state) => {
        state.status = 'loading';
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.isLoading = false;
        state.user = action.payload;
        setUser(action.payload);
        state.error = null;
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.status = 'failed';
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { logout, clearError, setCredentials } = authSlice.actions;
export default authSlice.reducer;
